import React, { useState, useCallback } from 'react';
import { ConnectionType, TransferMethod, ConnectionConfigWire, ConnectionTestResult } from '../../types/connection';
import { connectionsApi } from '../../services/api/connections';
import { TestResultPanel } from './TestResultPanel';
import { StatusIndicator } from '../shared/StatusIndicators';
import toast from 'react-hot-toast';

interface PreCreationTestPanelProps {
  connectionType: ConnectionType;
  transferMethod: TransferMethod;
  config: Partial<ConnectionConfigWire>;
  connectorArn?: string;  // For edit mode - existing connector
  userArn?: string;       // For edit mode - existing user
  isEditMode?: boolean;
  onTestComplete?: (result: { isValid: boolean; testResults?: ConnectionTestResult }) => void;
}

interface ValidationResult {
  isValid: boolean;
  validationErrors: string[];
  testResults?: ConnectionTestResult;
  testPerformed: boolean;
}

export function PreCreationTestPanel({
  connectionType,
  transferMethod,
  config,
  connectorArn,
  userArn,
  isEditMode = false,
  onTestComplete
}: PreCreationTestPanelProps) {
  const [isTesting, setIsTesting] = useState(false);
  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);

  const handleTestConfig = useCallback(async () => {
    setIsTesting(true);
    setValidationResult(null);

    try {
      const result = await connectionsApi.testConnectionConfig({
        connectionType,
        transferMethod,
        config,
        connectorArn,
        userArn
      });

      setValidationResult(result);

      // Show toast notification
      if (result.isValid) {
        if (result.testPerformed && result.testResults?.success) {
          toast.success('Configuration valid and connection test passed!');
        } else if (result.testPerformed && !result.testResults?.success) {
          toast.error('Configuration valid but connection test failed');
        } else {
          toast.success('Configuration validated successfully');
        }
      } else {
        toast.error(`Configuration validation failed: ${result.validationErrors.join(', ')}`);
      }

      // Callback
      if (onTestComplete) {
        onTestComplete({
          isValid: result.isValid,
          testResults: result.testResults
        });
      }
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to validate configuration';
      toast.error(errorMessage);
      setValidationResult({
        isValid: false,
        validationErrors: [errorMessage],
        testPerformed: false
      });
    } finally {
      setIsTesting(false);
    }
  }, [connectionType, transferMethod, config, connectorArn, userArn, onTestComplete]);

  const canTest = connectionType && transferMethod && config;

  const getButtonText = () => {
    if (isTesting) return 'Validating...';
    if (transferMethod === 'api') {
      return 'Validate & Test HTTP';
    }
    if (isEditMode && (connectorArn || userArn)) {
      return 'Validate & Test';
    }
    return 'Validate Configuration';
  };

  const getInfoText = () => {
    if (transferMethod === 'api') {
      return 'This will validate the HTTP configuration and test connectivity to the partner endpoint using the provided credentials.';
    }
    if (isEditMode && (connectorArn || userArn)) {
      return 'This will validate the configuration and test connectivity using the existing connector.';
    }
    if (connectionType === 'outbound') {
      return 'This will validate the configuration. Actual connectivity testing will occur after the connection is created.';
    }
    return 'This will validate the configuration for inbound connections.';
  };

  return (
    <div className="bg-neutral-50 dark:bg-neutral-800/50 border border-neutral-200 dark:border-neutral-700 rounded-lg p-4 my-4">
      <div className="mb-4">
        <h4 className="text-sm font-semibold text-neutral-800 dark:text-neutral-200 mb-2">
          Configuration Validation
        </h4>
        <p className="text-sm text-neutral-600 dark:text-neutral-400 leading-relaxed">
          {getInfoText()}
        </p>
      </div>

      <div className="mb-4">
        <button
          className={`
            inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium
            bg-neutral-100 dark:bg-neutral-700 border border-neutral-300 dark:border-neutral-600
            text-neutral-700 dark:text-neutral-200
            hover:bg-neutral-200 dark:hover:bg-neutral-600
            disabled:opacity-60 disabled:cursor-not-allowed
            transition-colors duration-150
            ${isTesting ? 'cursor-wait' : ''}
          `}
          onClick={handleTestConfig}
          disabled={!canTest || isTesting}
        >
          {isTesting && (
            <span className="w-3.5 h-3.5 border-2 border-neutral-400 dark:border-neutral-500 border-t-neutral-700 dark:border-t-neutral-200 rounded-full animate-spin" />
          )}
          {getButtonText()}
        </button>
      </div>

      {validationResult && (
        <div className="mt-4">
          {!validationResult.isValid && validationResult.validationErrors.length > 0 && (
            <div className="bg-danger-500/10 dark:bg-danger-500/20 border border-danger-500/20 dark:border-danger-500/30 rounded-lg p-3">
              <StatusIndicator status="error" label="Validation Failed" />
              <ul className="mt-3 pl-5 text-sm text-danger-600 dark:text-danger-400 list-disc space-y-1">
                {validationResult.validationErrors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          )}

          {validationResult.isValid && !validationResult.testPerformed && (
            <div className="bg-success-500/10 dark:bg-success-500/20 border border-success-500/20 dark:border-success-500/30 rounded-lg p-3">
              <StatusIndicator status="healthy" label="Configuration Valid" />
              <p className="mt-2 text-sm text-neutral-600 dark:text-neutral-400">
                The configuration is valid. Connection will be tested after creation.
              </p>
            </div>
          )}

          {validationResult.isValid && validationResult.testPerformed && validationResult.testResults && (
            <div className="mt-3">
              <TestResultPanel
                result={validationResult.testResults}
                showStages={true}
                compact={false}
                connectionType={connectionType}
                transferMethod={transferMethod}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default PreCreationTestPanel;
